import { useState, useEffect } from 'react';
import axios from 'axios';

const API = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
  ? 'http://localhost:8001/api'
  : '/api';

function CryptoSettings() {
  const [enabled, setEnabled] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [cryptoType, setCryptoType] = useState('USDT-TRC20');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');

  const token = localStorage.getItem('token');
  const axiosConfig = {
    headers: { 'Authorization': `Bearer ${token}` }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/crypto-info`);
      console.log('🔍 Fetched crypto settings:', response.data);
      
      const enabledValue = response.data.enabled || false;
      const walletValue = response.data.wallet_address || '';
      const qrValue = response.data.qr_code || '';
      const typeValue = response.data.crypto_type || 'USDT-TRC20';
      
      console.log('📝 Setting state:', { enabled: enabledValue, wallet: walletValue, qr: qrValue, type: typeValue });
      
      setEnabled(enabledValue);
      setWalletAddress(walletValue);
      setQrCode(qrValue);
      setCryptoType(typeValue);
      
      console.log(' State updated!');
    } catch (err) {
      console.error(' Error fetching crypto settings:', err);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    console.log('📁 File selected:', file.name, file.type, file.size);

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setMessage('❌ Sadece PNG, JPG veya WEBP formatları destekleniyor');
      console.error('Invalid file type:', file.type);
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage('❌ Dosya boyutu 5MB\'dan küçük olmalıdır');
      console.error('File too large:', file.size);
      return;
    }

    setUploading(true);
    setMessage('⏳ QR kod yükleniyor...');
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      console.log('📤 Uploading to:', `${API}/admin/upload-crypto-qr`);
      
      const response = await axios.post(`${API}/admin/upload-crypto-qr`, formData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log(' Upload response:', response.data);
      
      const uploadedQrUrl = response.data.qr_url;
      setQrCode(uploadedQrUrl);
      
      setMessage('✅ QR kod yüklendi ve kaydedildi!');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error(' QR upload error:', err);
      console.error('Error details:', err.response?.data);
      setMessage(`❌ Hata: ${err.response?.data?.detail || err.message}`);
    }
    setUploading(false);
  };

  const handleSave = async () => {
    setLoading(true);
    setMessage('');
    try {
      await axios.put(`${API}/admin/crypto-settings`, {
        enabled: enabled,
        wallet_address: walletAddress,
        qr_code: qrCode,
        crypto_type: cryptoType
      }, axiosConfig);
      
      setMessage('✅ Kripto ayarları başarıyla kaydedildi');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Error saving crypto settings:', err);
      setMessage('❌ Ayarlar kaydedilemedi');
    }
    setLoading(false);
  };

  return (
    <div className="bg-gray-800 rounded-lg p-6">
      <h2 className="text-2xl font-bold mb-6">₿ Kripto Ödeme Ayarları</h2>
      
      {message && (
        <div className={`mb-4 p-3 rounded-lg ${message.startsWith('✅') ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'}`}>
          {message}
        </div>
      )}

      <div className="space-y-6">
        {/* Enable/Disable */}
        <div className="flex items-center gap-4">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="w-5 h-5 rounded bg-gray-700 border-gray-600 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-3 text-white font-semibold">Kripto Ödeme Sistemini Aktif Et</span>
          </label>
        </div>

        {/* Crypto Type */}
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-300">Kripto Para Türü</label>
          <select
            value={cryptoType}
            onChange={(e) => setCryptoType(e.target.value)}
            className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={!enabled}
          >
            <option value="USDT-TRC20">USDT (TRC20)</option>
            <option value="USDT-ERC20">USDT (ERC20)</option>
            <option value="BTC">Bitcoin (BTC)</option>
            <option value="ETH">Ethereum (ETH)</option>
            <option value="BNB">Binance Coin (BNB)</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">Kullanıcıların ödeme yapabileceği kripto para birimi</p>
        </div>

        {/* Wallet Address */}
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-300">Cüzdan Adresi *</label>
          <input
            type="text"
            value={walletAddress}
            onChange={(e) => setWalletAddress(e.target.value)}
            className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
            placeholder="TXh7S9aK3mVYqJ8fPnB2x4wR5t6cN1m9L0"
            disabled={!enabled}
          />
          <p className="text-xs text-gray-500 mt-1">Ödeme alacağınız cüzdan adresi</p>
        </div>

        {/* QR Code Upload */}
        <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">QR Kod Yükle</label>
              <div className="flex gap-3">
                <label className="flex-1 cursor-pointer">
                  <div className="w-full bg-gray-700 hover:bg-gray-600 text-white px-4 py-3 rounded-lg border border-gray-600 text-center transition-colors">
                    {uploading ? '⏳ Yükleniyor...' : '📁 Dosya Seç'}
                  </div>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={handleFileUpload}
                    disabled={uploading}
                    className="hidden"
                  />
                </label>
                {qrCode && (
                  <button
                    type="button"
                    onClick={() => setQrCode('')}
                    className="px-4 py-3 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                    title="QR Kodu Kaldır"
                  >
                    🗑️
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">PNG, JPG veya WEBP formatı, max 5MB</p>
            </div>

            {/* QR Code Preview */}
            {qrCode && (
              <div className="bg-gray-700 p-4 rounded-lg border border-gray-600">
                <p className="text-sm text-gray-400 mb-3 font-semibold">📷 QR Kod Önizleme:</p>
                <div className="flex justify-center">
                  <img 
                    src={qrCode} 
                    alt="QR Code" 
                    className="max-w-xs w-full rounded-lg shadow-lg" 
                    onError={(e) => {
                      console.error(' QR Image failed to load:', qrCode);
                      e.target.style.display = 'none';
                      setMessage(`❌ QR yüklenemedi: ${qrCode}`);
                    }} 
                  />
                </div>
              </div>
            )}

        {/* Info Box */}
        <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-4">
          <div className="font-semibold mb-2 text-blue-400">📌 Bilgilendirme:</div>
          <ul className="space-y-1 text-sm text-gray-300">
            <li>• Kripto ödeme sistemi aktif olduğunda, kullanıcılar para yatırma sayfasında kripto seçeneğini görecek</li>
            <li>• QR kod eklemek kullanıcıların cüzdan adresini kolayca taramasını sağlar</li>
            <li>• Cüzdan adresinin doğru olduğundan emin olun</li>
            <li>• Kripto ödemeler manuel onay gerektirir (admin panelden)</li>
            {!enabled && <li className="text-yellow-400">⚠️ Kripto ödeme sistemini aktif etmek için yukarıdaki kutucuğu işaretleyin</li>}
          </ul>
        </div>

        {/* Save Button */}
        <button
          onClick={handleSave}
          disabled={loading || (enabled && !walletAddress)}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed px-6 py-3 rounded-lg font-bold transition-colors"
        >
          {loading ? 'Kaydediliyor...' : '💾 Ayarları Kaydet'}
        </button>
      </div>
    </div>
  );
}

export default CryptoSettings;
